import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, CheckCircle2, AlertTriangle, Zap } from 'lucide-react';
interface ExitIntentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
}
export function ExitIntentPopup({
  isOpen,
  onClose,
  onAccept,
  onDecline
}: ExitIntentPopupProps) {
  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-207">
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-208">
        {/* Dark overlay */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" data-id="element-209" />

        {/* Popup Content */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-210">
          {/* Close button */}
          <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-200 transition-colors z-10 bg-slate-800/50 rounded-full p-1" aria-label="Close popup" data-id="element-211">
            <X className="w-5 h-5" data-id="element-212" />
          </button>

          {/* Scrollable content area */}
          <div className="overflow-y-auto flex-1" data-id="element-213">
            {/* Header Banner */}
            <div className="bg-amber-500/10 border-b border-amber-500/20 p-6 text-center relative overflow-hidden" data-id="element-214">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600" data-id="element-215" />

              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-216">
                <AlertTriangle className="w-3.5 h-3.5" data-id="element-217" />
                Wait! Special One-Time Offer
              </div>

              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-slate-50 mb-2 leading-tight" data-id="element-218">
                Don't Miss Out On Faster Results
              </h2>
              <p className="text-amber-400 font-medium text-lg" data-id="element-219">
                Unlock the complete biological reset system before you checkout.
              </p>
            </div>

            {/* Body Content */}
            <div className="p-6 sm:p-8" data-id="element-220">
              <div className="flex flex-col sm:flex-row gap-6 items-start mb-8" data-id="element-221">
                {/* Flashing Badge */}
                <div className="shrink-0 mx-auto sm:mx-0" data-id="element-222">
                  <div className="flash-badge w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex flex-col items-center justify-center text-slate-900 shadow-[0_0_30px_rgba(245,158,11,0.3)] border-4 border-slate-900" data-id="element-223">
                    <span className="text-2xl font-black leading-none" data-id="element-224">
                      30%
                    </span>
                    <span className="text-sm font-bold uppercase tracking-wider" data-id="element-225">
                      OFF
                    </span>
                  </div>
                </div>

                {/* Copy */}
                <div className="text-center sm:text-left space-y-3" data-id="element-226">
                  <p className="text-slate-300 text-lg leading-relaxed" data-id="element-227">
                    Melt stubborn stress-fat and master your vagus nerve even
                    faster with our{' '}
                    <span className="text-slate-50 font-semibold" data-id="element-228">
                      Complete Cortisol Switch Bundle
                    </span>
                    .
                  </p>
                  <p className="text-slate-400 text-sm" data-id="element-229">
                    We've bundled the main manual with our top two accelerators
                    to give you the ultimate biological advantage.
                  </p>
                </div>
              </div>

              {/* What's Included List */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 mb-8" data-id="element-230">
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-4" data-id="element-231">
                  Here's what you get in the bundle:
                </p>
                <ul className="space-y-3" data-id="element-232">
                  <li className="flex items-start gap-3" data-id="element-233">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-234" />
                    <div data-id="element-235">
                      <span className="text-slate-50 font-medium block" data-id="element-236">
                        The Cortisol Switch + 3 Free Bonuses
                      </span>
                      <span className="text-slate-400 text-xs" data-id="element-237">
                        The core 40-page digital manual & protocol
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-238">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-239" />
                    <div data-id="element-240">
                      <span className="text-slate-50 font-medium block" data-id="element-241">
                        The Cortisol-Safe Elite Motion Bible
                      </span>
                      <span className="text-slate-400 text-xs" data-id="element-242">
                        Desk Resets + At-Home Sculpt Routine
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-243">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-244" />
                    <div data-id="element-245">
                      <span className="text-slate-50 font-medium block" data-id="element-246">
                        Stress-Proof Dining Out Guide
                      </span>
                      <span className="text-slate-400 text-xs" data-id="element-247">
                        Safe Orders + Social Buffer Strategy
                      </span>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Pricing Block */}
              <div className="text-center mb-8" data-id="element-248">
                <div className="inline-flex items-center justify-center gap-3 mb-2" data-id="element-249">
                  <span className="text-2xl text-slate-500 line-through font-medium" data-id="element-250">
                    $24.39
                  </span>
                  <span className="text-5xl font-bold text-amber-400" data-id="element-251">
                    $17.07
                  </span>
                </div>
                <div className="flex items-center justify-center gap-1.5 text-emerald-400 text-sm font-medium" data-id="element-252">
                  <Lock className="w-3.5 h-3.5" data-id="element-253" />
                  <span data-id="element-254">Special price only available on this screen</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-4" data-id="element-255">
                <button onClick={onAccept} className="cta-pulse w-full flex items-center justify-center gap-2 px-8 py-4 text-lg font-bold text-slate-900 bg-amber-500 rounded-xl hover:bg-amber-400 transition-colors duration-300 shadow-[0_0_20px_rgba(245,158,11,0.2)]" data-id="element-256">
                  <Zap className="w-5 h-5" data-id="element-257" />
                  YES! I want the Complete Package for $17.07
                </button>

                <button onClick={onDecline} className="w-full px-6 py-3 text-sm font-medium text-slate-400 hover:text-slate-300 transition-colors" data-id="element-258">
                  No thanks, continue without the extras →
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}